import React, { useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { 
  Save, 
  X, 
  Upload, 
  Image as ImageIcon, 
  Plus, 
  Minus,
  Star,
  DollarSign,
  Package,
  FileText,
  Settings
} from 'lucide-react';

const ProductForm = ({ product, onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    name: product?.name || '',
    category: product?.category || 'coffee',
    price: product?.price || '',
    originalPrice: product?.originalPrice || '',
    description: product?.description || '',
    longDescription: product?.longDescription || '',
    features: product?.features || [''],
    specifications: product?.specifications || {
      dimensions: '',
      weight: '',
      material: '',
      finish: '',
      warranty: ''
    },
    inStock: product?.inStock !== undefined ? product.inStock : true,
    featured: product?.featured || false,
    rating: product?.rating || 4.5,
    reviews: product?.reviews || 0,
    image: product?.image || '',
    images: product?.images || ['']
  });

  const [currentTab, setCurrentTab] = useState('basic');
  const [errors, setErrors] = useState({});
  const fileInputRef = useRef(null);

  const categories = [
    { id: 'dining', name: 'Dining Tables' },
    { id: 'coffee', name: 'Coffee Tables' },
    { id: 'console', name: 'Console Tables' },
    { id: 'accent', name: 'Accent Tables' },
    { id: 'bar', name: 'Bar Tops' },
    { id: 'office', name: 'Office Furniture' }
  ];

  const tabs = [
    { id: 'basic', name: 'Basic Info', icon: Package },
    { id: 'images', name: 'Images', icon: ImageIcon },
    { id: 'details', name: 'Details', icon: FileText },
    { id: 'specs', name: 'Specifications', icon: Settings }
  ];

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleSpecificationChange = (key, value) => {
    setFormData(prev => ({
      ...prev,
      specifications: {
        ...prev.specifications,
        [key]: value
      }
    }));
  };

  const handleFeatureChange = (index, value) => {
    const newFeatures = [...formData.features];
    newFeatures[index] = value;
    setFormData(prev => ({ ...prev, features: newFeatures }));
  };

  const addFeature = () => {
    setFormData(prev => ({
      ...prev,
      features: [...prev.features, '']
    }));
  };

  const removeFeature = (index) => {
    setFormData(prev => ({
      ...prev,
      features: prev.features.filter((_, i) => i !== index)
    }));
  };

  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    
    files.forEach(file => {
      const reader = new FileReader();
      reader.onload = (event) => {
        const imageUrl = event.target.result;
        setFormData(prev => {
          const newImages = [...prev.images];
          const emptyIndex = newImages.findIndex(img => img === '');
          
          if (emptyIndex !== -1) {
            newImages[emptyIndex] = imageUrl;
          } else {
            newImages.push(imageUrl);
          }
          
          return {
            ...prev,
            images: newImages,
            image: prev.image || imageUrl // Set as main image if none exists
          };
        });
      };
      reader.readAsDataURL(file);
    });
  };

  const addImageSlot = () => {
    setFormData(prev => ({
      ...prev,
      images: [...prev.images, '']
    }));
  };

  const removeImage = (index) => {
    setFormData(prev => {
      const newImages = prev.images.filter((_, i) => i !== index);
      return {
        ...prev,
        images: newImages,
        image: prev.image === prev.images[index] ? newImages[0] || '' : prev.image
      };
    });
  };

  const setMainImage = (imageUrl) => {
    setFormData(prev => ({ ...prev, image: imageUrl }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) newErrors.name = 'Product name is required';
    if (!formData.price) newErrors.price = 'Price is required';
    if (!formData.description.trim()) newErrors.description = 'Description is required';
    if (!formData.image) newErrors.image = 'At least one image is required';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    // Clean up features (remove empty ones)
    const cleanedFeatures = formData.features.filter(feature => feature.trim() !== '');
    
    // Clean up images (remove empty ones)
    const cleanedImages = formData.images.filter(image => image.trim() !== '');

    const productData = {
      ...formData,
      features: cleanedFeatures,
      images: cleanedImages,
      price: parseFloat(formData.price),
      originalPrice: parseFloat(formData.originalPrice) || parseFloat(formData.price),
      rating: parseFloat(formData.rating),
      reviews: parseInt(formData.reviews) || 0
    };

    onSave(productData);
  };

  return (
    <div className="min-h-screen bg-dark-950 p-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-white">
              {product ? 'Edit Product' : 'Add New Product'}
            </h1>
            <p className="text-dark-300">
              {product ? 'Update product information' : 'Create a new product listing'}
            </p>
          </div>
          <button
            onClick={onCancel}
            className="p-3 text-dark-400 hover:text-white hover:bg-dark-800 rounded-lg transition-colors duration-200"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          {/* Tab Navigation */}
          <div className="bg-dark-800/50 backdrop-blur-md rounded-2xl border border-dark-700 mb-6">
            <div className="flex border-b border-dark-700">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setCurrentTab(tab.id)}
                  className={`flex items-center space-x-2 px-6 py-4 font-medium transition-colors duration-200 ${
                    currentTab === tab.id
                      ? 'text-accent-400 border-b-2 border-accent-400'
                      : 'text-dark-300 hover:text-white'
                  }`}
                >
                  <tab.icon className="h-5 w-5" />
                  <span>{tab.name}</span>
                </button>
              ))}
            </div>

            <div className="p-6">
              {/* Basic Info Tab */}
              {currentTab === 'basic' && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="space-y-6"
                >
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Product Name */}
                    <div>
                      <label className="block text-white font-medium mb-2">
                        Product Name *
                      </label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        className={`w-full bg-dark-800 border rounded-lg px-4 py-3 text-white placeholder-dark-400 focus:outline-none transition-colors duration-300 ${
                          errors.name ? 'border-red-500' : 'border-dark-600 focus:border-accent-500'
                        }`}
                        placeholder="Enter product name"
                      />
                      {errors.name && <p className="text-red-400 text-sm mt-1">{errors.name}</p>}
                    </div>

                    {/* Category */}
                    <div>
                      <label className="block text-white font-medium mb-2">
                        Category *
                      </label>
                      <select
                        name="category"
                        value={formData.category}
                        onChange={handleInputChange}
                        className="w-full bg-dark-800 border border-dark-600 rounded-lg px-4 py-3 text-white focus:border-accent-500 focus:outline-none"
                      >
                        {categories.map(cat => (
                          <option key={cat.id} value={cat.id}>{cat.name}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Price */}
                    <div>
                      <label className="block text-white font-medium mb-2">
                        Price ($) *
                      </label>
                      <div className="relative">
                        <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-dark-400" />
                        <input
                          type="number"
                          name="price"
                          value={formData.price}
                          onChange={handleInputChange}
                          className={`w-full bg-dark-800 border rounded-lg pl-12 pr-4 py-3 text-white placeholder-dark-400 focus:outline-none transition-colors duration-300 ${
                            errors.price ? 'border-red-500' : 'border-dark-600 focus:border-accent-500'
                          }`}
                          placeholder="0.00"
                          min="0"
                          step="0.01"
                        />
                      </div>
                      {errors.price && <p className="text-red-400 text-sm mt-1">{errors.price}</p>}
                    </div>

                    {/* Original Price */}
                    <div>
                      <label className="block text-white font-medium mb-2">
                        Original Price ($)
                      </label>
                      <div className="relative">
                        <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-dark-400" />
                        <input
                          type="number"
                          name="originalPrice"
                          value={formData.originalPrice}
                          onChange={handleInputChange}
                          className="w-full bg-dark-800 border border-dark-600 rounded-lg pl-12 pr-4 py-3 text-white placeholder-dark-400 focus:border-accent-500 focus:outline-none"
                          placeholder="0.00"
                          min="0"
                          step="0.01"
                        />
                      </div>
                      <p className="text-dark-400 text-sm mt-1">Leave empty if no discount</p>
                    </div>
                  </div>

                  {/* Description */}
                  <div>
                    <label className="block text-white font-medium mb-2">
                      Short Description *
                    </label>
                    <textarea
                      name="description"
                      value={formData.description}
                      onChange={handleInputChange}
                      rows={3}
                      className={`w-full bg-dark-800 border rounded-lg px-4 py-3 text-white placeholder-dark-400 focus:outline-none transition-colors duration-300 resize-none ${
                        errors.description ? 'border-red-500' : 'border-dark-600 focus:border-accent-500'
                      }`}
                      placeholder="Brief product description for listings"
                    />
                    {errors.description && <p className="text-red-400 text-sm mt-1">{errors.description}</p>}
                  </div>

                  {/* Status Checkboxes */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <label className="flex items-center space-x-3 cursor-pointer">
                      <input
                        type="checkbox"
                        name="inStock"
                        checked={formData.inStock}
                        onChange={handleInputChange}
                        className="rounded border-dark-600 bg-dark-800 text-accent-500 focus:ring-accent-500"
                      />
                      <span className="text-white font-medium">In Stock</span>
                    </label>

                    <label className="flex items-center space-x-3 cursor-pointer">
                      <input
                        type="checkbox"
                        name="featured"
                        checked={formData.featured}
                        onChange={handleInputChange}
                        className="rounded border-dark-600 bg-dark-800 text-accent-500 focus:ring-accent-500"
                      />
                      <span className="text-white font-medium">Featured Product</span>
                    </label>

                    <div>
                      <label className="block text-white font-medium mb-2">
                        Rating
                      </label>
                      <div className="flex items-center space-x-2">
                        <Star className="h-5 w-5 text-yellow-400" />
                        <input
                          type="number"
                          name="rating"
                          value={formData.rating}
                          onChange={handleInputChange}
                          className="w-20 bg-dark-800 border border-dark-600 rounded-lg px-3 py-2 text-white focus:border-accent-500 focus:outline-none"
                          min="0"
                          max="5"
                          step="0.1"
                        />
                      </div>
                    </div>
                  </div>
                </motion.div>
              )}

              {/* Images Tab */}
              {currentTab === 'images' && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="space-y-6"
                >
                  <div>
                    <h3 className="text-lg font-semibold text-white mb-4">Product Images</h3>
                    
                    {/* Upload Button */}
                    <div className="mb-6">
                      <input
                        ref={fileInputRef}
                        type="file"
                        multiple
                        accept="image/*"
                        onChange={handleImageUpload}
                        className="hidden"
                      />
                      <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        className="bg-gradient-to-r from-accent-500 to-accent-600 text-white px-6 py-3 rounded-lg font-medium hover:from-accent-600 hover:to-accent-700 transition-all duration-300 flex items-center space-x-2"
                      >
                        <Upload className="h-5 w-5" />
                        <span>Upload Images</span>
                      </button>
                      <p className="text-dark-400 text-sm mt-2">
                        Select multiple images. First image will be the main product image.
                      </p>
                    </div>

                    {/* Image Grid */}
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                      {formData.images.map((image, index) => (
                        <div
                          key={index}
                          className={`relative aspect-square rounded-lg border-2 transition-colors duration-200 ${
                            image === formData.image 
                              ? 'border-accent-500' 
                              : 'border-dark-600'
                          }`}
                        >
                          {image ? (
                            <>
                              <img
                                src={image}
                                alt={`Product ${index + 1}`}
                                className="w-full h-full object-cover rounded-lg"
                              />
                              
                              {/* Main Image Badge */}
                              {image === formData.image && (
                                <div className="absolute top-2 left-2 bg-accent-500 text-white text-xs px-2 py-1 rounded">
                                  Main
                                </div>
                              )}

                              {/* Actions */}
                              <div className="absolute top-2 right-2 flex space-x-1">
                                {image !== formData.image && (
                                  <button
                                    type="button"
                                    onClick={() => setMainImage(image)}
                                    className="bg-black/50 text-white p-1 rounded text-xs hover:bg-black/70"
                                  >
                                    Set Main
                                  </button>
                                )}
                                <button
                                  type="button"
                                  onClick={() => removeImage(index)}
                                  className="bg-red-500/80 text-white p-1 rounded hover:bg-red-500"
                                >
                                  <X className="h-3 w-3" />
                                </button>
                              </div>
                            </>
                          ) : (
                            <div className="w-full h-full bg-dark-700 rounded-lg flex items-center justify-center cursor-pointer hover:bg-dark-600 transition-colors duration-200">
                              <div className="text-center">
                                <ImageIcon className="h-8 w-8 text-dark-400 mx-auto mb-2" />
                                <p className="text-dark-400 text-sm">Click to add image</p>
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                      
                      {/* Add More Button */}
                      <button
                        type="button"
                        onClick={addImageSlot}
                        className="aspect-square rounded-lg border-2 border-dashed border-dark-600 flex items-center justify-center hover:border-accent-500 transition-colors duration-200"
                      >
                        <Plus className="h-8 w-8 text-dark-400" />
                      </button>
                    </div>

                    {errors.image && <p className="text-red-400 text-sm mt-2">{errors.image}</p>}
                  </div>
                </motion.div>
              )}

              {/* Details Tab */}
              {currentTab === 'details' && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="space-y-6"
                >
                  {/* Long Description */}
                  <div>
                    <label className="block text-white font-medium mb-2">
                      Detailed Description
                    </label>
                    <textarea
                      name="longDescription"
                      value={formData.longDescription}
                      onChange={handleInputChange}
                      rows={6}
                      className="w-full bg-dark-800 border border-dark-600 rounded-lg px-4 py-3 text-white placeholder-dark-400 focus:border-accent-500 focus:outline-none resize-none"
                      placeholder="Detailed product description for product pages"
                    />
                  </div>

                  {/* Features */}
                  <div>
                    <label className="block text-white font-medium mb-4">
                      Key Features
                    </label>
                    <div className="space-y-3">
                      {formData.features.map((feature, index) => (
                        <div key={index} className="flex items-center space-x-3">
                          <input
                            type="text"
                            value={feature}
                            onChange={(e) => handleFeatureChange(index, e.target.value)}
                            className="flex-1 bg-dark-800 border border-dark-600 rounded-lg px-4 py-3 text-white placeholder-dark-400 focus:border-accent-500 focus:outline-none"
                            placeholder={`Feature ${index + 1}`}
                          />
                          <button
                            type="button"
                            onClick={() => removeFeature(index)}
                            className="p-3 text-red-400 hover:bg-red-500/20 rounded-lg transition-colors duration-200"
                          >
                            <Minus className="h-4 w-4" />
                          </button>
                        </div>
                      ))}
                      <button
                        type="button"
                        onClick={addFeature}
                        className="flex items-center space-x-2 text-accent-400 hover:text-accent-300 transition-colors duration-200"
                      >
                        <Plus className="h-4 w-4" />
                        <span>Add Feature</span>
                      </button>
                    </div>
                  </div>

                  {/* Reviews Count */}
                  <div>
                    <label className="block text-white font-medium mb-2">
                      Number of Reviews
                    </label>
                    <input
                      type="number"
                      name="reviews"
                      value={formData.reviews}
                      onChange={handleInputChange}
                      className="w-32 bg-dark-800 border border-dark-600 rounded-lg px-4 py-3 text-white focus:border-accent-500 focus:outline-none"
                      min="0"
                    />
                  </div>
                </motion.div>
              )}

              {/* Specifications Tab */}
              {currentTab === 'specs' && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="space-y-6"
                >
                  <h3 className="text-lg font-semibold text-white mb-4">Product Specifications</h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {Object.entries(formData.specifications).map(([key, value]) => (
                      <div key={key}>
                        <label className="block text-white font-medium mb-2 capitalize">
                          {key.replace(/([A-Z])/g, ' $1').trim()}
                        </label>
                        <input
                          type="text"
                          value={value}
                          onChange={(e) => handleSpecificationChange(key, e.target.value)}
                          className="w-full bg-dark-800 border border-dark-600 rounded-lg px-4 py-3 text-white placeholder-dark-400 focus:border-accent-500 focus:outline-none"
                          placeholder={`Enter ${key.toLowerCase()}`}
                        />
                      </div>
                    ))}
                  </div>
                </motion.div>
              )}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end space-x-4">
            <button
              type="button"
              onClick={onCancel}
              className="px-6 py-3 border border-dark-600 text-dark-300 rounded-lg hover:bg-dark-800 hover:text-white transition-colors duration-300"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="bg-gradient-to-r from-accent-500 to-accent-600 text-white px-8 py-3 rounded-lg font-medium hover:from-accent-600 hover:to-accent-700 transition-all duration-300 flex items-center space-x-2"
            >
              <Save className="h-5 w-5" />
              <span>{product ? 'Update Product' : 'Create Product'}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ProductForm;